/**
 * AnalyticsRouteTracker -- fires a `page_view` event (plus, where a route
 * maps cleanly to one of the Phase-1 named "Application" events, a more
 * specific `*_viewed` event) on every route change. Mounted once at the
 * router level (see App.tsx) so page-view tracking is never duplicated
 * per-page-component, per the "reusable utility, not duplicated tracking
 * code" requirement.
 *
 * NOTE: three Phase-1 event names -- market_scanner_viewed,
 * backtesting_viewed, strategy_simulation_viewed -- don't have a 1:1
 * top-level route in this app's current (post-redesign) routing; those
 * features live as tabs inside other pages rather than their own path.
 * Routes for those still emit the generic `page_view` here. Wire the
 * specific named event directly in the relevant tab's onChange handler if
 * finer-grained tracking for those particular tabs is wanted later.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/services/analyticsService";

const PATH_EVENT_MAP: Record<string, string> = {
  "/dashboard": "dashboard_viewed",
  "/strategies": "algo_strategy_viewed",
  "/options-analyst": "options_dashboard_viewed",
};

const QUERY_TAB_EVENT_MAP: Record<string, Record<string, string>> = {
  "/investing-idea": {
    "market-opportunities": "market_opportunities_viewed",
    "stock-data": "stock_data_viewed",
    "portfolio-tracker": "portfolio_viewed",
  },
};

export function AnalyticsRouteTracker() {
  const location = useLocation();
  const lastTrackedKey = useRef<string | null>(null);

  useEffect(() => {
    const key = `${location.pathname}${location.search}`;
    if (lastTrackedKey.current === key) return;
    lastTrackedKey.current = key;

    trackEvent("page_view");

    const tab = new URLSearchParams(location.search).get("tab");
    const specificEvent =
      (tab && QUERY_TAB_EVENT_MAP[location.pathname]?.[tab]) ?? PATH_EVENT_MAP[location.pathname];
    if (specificEvent) {
      trackEvent(specificEvent);
    }
  }, [location.pathname, location.search]);

  return null;
}

export default AnalyticsRouteTracker;
